/**
 * The single source of truth for "which pages actually exist in Spanish".
 *
 * This list drives, all at once:
 *   - hreflang tags (lib/components/Head.tsx)
 *   - the `noindex` fallback on untranslated /es/* URLs (same file)
 *   - `xhtml:link rel="alternate"` entries in the sitemap
 *   - the returning-visitor cookie redirects in next.config.js
 *   - whether the language switcher and the language banner offer a swap
 *
 * Plain CommonJS on purpose: next.config.js has to `require` it, and it cannot
 * load TypeScript. `lib/i18n/routes.ts` re-exports this with types for app code.
 *
 * Only add a path once its page is fully translated. Listing a page here makes
 * search engines treat /es/<path> as a legitimate Spanish alternate, so an
 * entry whose body is still English is an active SEO mistake, not a to-do.
 * Untranslated /es/* URLs are safe by default -- they get `noindex`.
 *
 * `/data/uscis-status-explorer/*` was listed here and has been withdrawn (F51).
 * The USCIS status messages are the substance of those pages and exist only in
 * English, so /es was never a legitimate alternate however well the surrounding
 * UI read. The Spanish strings stay: the pages still render in Spanish for anyone
 * who switches, they are just no longer advertised or indexed.
 *
 * Nothing uses the `/*` pattern at the moment. `isTranslatedRoute` and
 * `routeToRedirectParts` still support it on purpose -- it was measured (F38), and
 * a subtree of pages sharing one component is a shape that will recur.
 */

/** @type {string[]} Locale-agnostic pathnames, no locale prefix, no trailing slash. */
const translatedRoutes = [
  // Body copy, navigation and footer are fully Spanish. What stays English is
  // deliberate: user review quotes (D7), titles of English-only resource
  // articles (F13), and the Sanity promotion banners, which are CMS content
  // outside this project's scope (F14). Primary content is translated, so /es/
  // is a legitimate alternate rather than a duplicate.
  "/",
  // Same basis as "/": what stays English is the review quotes and the titles of
  // English-only resource articles. No CMS banners on this page.
  "/case-tracker",
  // The only English left is the email address and the two job titles, which
  // link to English-language AngelList postings (F13).
  "/about",
  // Marketing copy written for this page, so there is no CMS or user content to
  // lag behind. The five resource links keep their English headlines, since the
  // articles they point at exist only in English (F13), and the in-app tab names
  // in the step list stay English because that is what the visitor has to tap.
  "/mock-interview",
  // Listed for the cookie redirect alone, not for search. The page is `noindex`
  // and excluded from the sitemap, and it renders neither the language switcher
  // nor the project `Head` component, so hreflang, the sitemap alternates and
  // the switcher are all no-ops for it. What remains is the returning-visitor
  // rule in next.config.js, and since the page is reached from emails and
  // in-app links rather than from navigation, that redirect is the only way
  // someone who picked Spanish ever lands on the Spanish copy.
  "/app-review",
];

/**
 * Exact match, or prefix match for entries ending in `/*`.
 *
 * @param {string} pathname Locale-agnostic pathname, e.g. "/citizenship"
 * @returns {boolean}
 */
function isTranslatedRoute(pathname) {
  const path = normalizePathname(pathname);
  return translatedRoutes.some((route) =>
    route.endsWith("/*")
      ? // "/a/b/*" matches "/a/b/anything" but not "/a/b" itself
        path.startsWith(route.slice(0, -1))
      : path === route,
  );
}

/**
 * Splits a whitelist entry into the pieces next.config.js needs to build a
 * redirect rule: Next expresses a wildcard as `:path*`, not `*`.
 *
 * @param {string} route
 * @returns {{ base: string, suffix: string }}
 */
function routeToRedirectParts(route) {
  if (route.endsWith("/*")) {
    return { base: route.slice(0, -2), suffix: "/:path*" };
  }
  return { base: route === "/" ? "" : route, suffix: "" };
}

/**
 * Trims a trailing slash so "/about/" and "/about" are the same route.
 * Leaves "/" alone.
 *
 * @param {string} pathname
 * @returns {string}
 */
function normalizePathname(pathname) {
  if (!pathname) return "/";
  const [withoutQuery] = pathname.split(/[?#]/);
  if (withoutQuery.length > 1 && withoutQuery.endsWith("/")) {
    return withoutQuery.slice(0, -1);
  }
  return withoutQuery;
}

module.exports = {
  translatedRoutes,
  isTranslatedRoute,
  normalizePathname,
  routeToRedirectParts,
};
